// Every pass of a person in a recording, in time order: the crop of their figure, who they are once
// their face is confirmed (or that no usable face was seen), the events they are part of, and what
// the vision model saw of them. "Play pass" runs their pass in the player with their box emphasised;
// a click on the row opens it in place, large: the figure with the face in its corner and every fact.
// Pure: the people, their events and the play action arrive as props.
import { Play, User } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { describeQualityFlags } from '@/components/faces/format'
import { Thumbnail } from '@/components/humint/Thumbnail'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { wallSpan } from '@/features/document-overlay/recording-time'
import type { VideoEvent, VideoPerson } from '@/services/api/video'
import { cn } from '@/utils/cn'
import { formatVideoTime } from '@/utils/video-file'
import { CropDetail, type CropFact } from './CropDetail'
import { useEventKindLabel, WallSpan } from './VideoEventsInRecording'

const COPY = 'documents.detail.people'
const CROP = 'documents.detail.crop'
/** How many passes the section lists before it offers the rest. */
const FIRST = 20

/** The events a pass is part of: a person's own (staying, after hours, a crowd) and a threat in their scene. */
function eventsOf(person: VideoPerson, events: readonly VideoEvent[]): VideoEvent[] {
  return events.filter(
    event =>
      (event.label === 'person' || event.label === 'scene') &&
      event.track_ids.includes(person.track_id)
  )
}

export interface PeopleInRecordingProps {
  people: readonly VideoPerson[]
  events: readonly VideoEvent[]
  selectedTrackId: number | null
  /** "Play pass" on a person: the player runs their pass. Given only when a player is on the page. */
  onPlayTrack?: (trackId: number) => void
  /** When the recording starts (ISO 8601): each pass then shows its time on the camera's clock too. */
  recordedAt?: string | null
}

/** The face of a pass beside its figure; none when the figure is the face (a pass read before figures were kept). */
function faceOf(person: VideoPerson): string | null {
  return person.face_crop_path && person.face_crop_path !== person.crop_path
    ? person.face_crop_path
    : null
}

/** The figure with the face docked in its corner: the row's picture. */
function FigureThumb({ person, alt }: { person: VideoPerson; alt: string }) {
  const face = faceOf(person)
  return (
    <span className="relative shrink-0">
      <Thumbnail url={person.crop_path} alt={alt} Icon={User} className="h-20 w-14 rounded-md" />
      {face ? (
        <Thumbnail
          url={face}
          alt=""
          Icon={User}
          className="absolute -right-1.5 -bottom-1.5 size-8 rounded-md ring-2 ring-card"
        />
      ) : null}
    </span>
  )
}

/** Everything known of a pass, for its large view. */
function usePersonFacts(
  person: VideoPerson,
  events: readonly VideoEvent[],
  recordedAt: string | null
): CropFact[] {
  const { t, i18n } = useTranslation()
  const kindLabel = useEventKindLabel()
  const wall = wallSpan(recordedAt, person.t_start, person.t_end, i18n.language)
  const facts: CropFact[] = [
    {
      label: t(`${CROP}.recording`),
      value: t(`${COPY}.span`, {
        from: formatVideoTime(person.t_start),
        to: formatVideoTime(person.t_end),
      }),
    },
  ]
  if (wall) facts.push({ label: t(`${CROP}.cameraClock`), value: wall })
  facts.push(
    { label: t(`${CROP}.duration`), value: formatVideoTime(person.t_end - person.t_start) },
    { label: t(`${CROP}.track`), value: `#${person.track_id}` },
    {
      label: t(`${CROP}.face`),
      value: person.face_id
        ? describeQualityFlags(person.quality_flags, t, t(`${CROP}.faceUsable`))
        : t(`${CROP}.faceNone`),
    },
    { label: t(`${CROP}.identity`), value: person.identity_name ?? t(`${CROP}.unassigned`) }
  )
  if (events.length > 0) {
    facts.push({
      label: t(`${CROP}.events`),
      value: (
        <ul className="flex flex-col gap-1">
          {events.map(event => (
            <li key={event.event_id}>
              <span className="font-medium">{kindLabel(event.kind)}</span>{' '}
              <span className="text-muted-foreground">
                ({t(`documents.detail.videoEvents.verdict.${event.verdict}`)})
              </span>
              {event.reason ? (
                <span className="block text-muted-foreground">{event.reason}</span>
              ) : null}
            </li>
          ))}
        </ul>
      ),
    })
  }
  return facts
}

function PersonRow({
  person,
  events,
  selected,
  expanded,
  onToggle,
  onPlayTrack,
  recordedAt,
}: {
  person: VideoPerson
  events: readonly VideoEvent[]
  selected: boolean
  expanded: boolean
  onToggle: () => void
  onPlayTrack?: (trackId: number) => void
  recordedAt: string | null
}) {
  const { t, i18n } = useTranslation()
  const kindLabel = useEventKindLabel()
  const facts = usePersonFacts(person, events, recordedAt)
  const alt = t(`${COPY}.alt`, { time: formatVideoTime(person.t_start) })
  const face = faceOf(person)
  return (
    <li
      className={cn(
        'flex flex-col gap-3 rounded-md border bg-card px-2 py-2',
        selected || expanded ? 'border-primary' : 'border-border'
      )}
      data-track={person.track_id}
      data-expanded={expanded ? 'true' : 'false'}
    >
      <div className="flex gap-3">
        <button
          type="button"
          aria-expanded={expanded}
          title={t(expanded ? `${CROP}.close` : `${CROP}.open`)}
          onClick={onToggle}
          className="flex min-w-0 flex-1 gap-3 rounded-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {expanded ? null : <FigureThumb person={person} alt={alt} />}
          <span className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="flex flex-wrap items-center gap-1.5">
              {person.identity_name ? (
                <Badge variant="success">{person.identity_name}</Badge>
              ) : (
                <Badge variant="outline">
                  {t(person.face_id ? `${COPY}.unassigned` : `${COPY}.noFace`)}
                </Badge>
              )}
              {events.map(event => (
                <Badge
                  key={event.event_id}
                  variant={event.verdict === 'confirmed' ? 'warning' : 'muted'}
                >
                  {kindLabel(event.kind)}
                </Badge>
              ))}
            </span>
            <span className="text-xs tabular-nums text-muted-foreground">
              {t(`${COPY}.span`, {
                from: formatVideoTime(person.t_start),
                to: formatVideoTime(person.t_end),
              })}
              <WallSpan span={wallSpan(recordedAt, person.t_start, person.t_end, i18n.language)} />
            </span>
            {person.description && !expanded ? (
              <span className="line-clamp-3 text-xs text-foreground">{person.description}</span>
            ) : null}
          </span>
        </button>
        {onPlayTrack ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="shrink-0 self-start"
            aria-label={t(`${COPY}.play`)}
            onClick={() => onPlayTrack(person.track_id)}
          >
            <Play className="size-4" aria-hidden />
            <span className="max-md:sr-only">{t(`${COPY}.play`)}</span>
          </Button>
        ) : null}
      </div>
      {expanded ? (
        <CropDetail
          shape="figure"
          image={{ url: person.crop_path, alt, Icon: User }}
          inset={face ? { url: face, alt: t(`${CROP}.faceAlt`) } : null}
          facts={facts}
        >
          {person.description ? (
            <p className="text-sm text-foreground">{person.description}</p>
          ) : null}
        </CropDetail>
      ) : null}
    </li>
  )
}

export function PeopleInRecording({
  people,
  events,
  selectedTrackId,
  onPlayTrack,
  recordedAt = null,
}: PeopleInRecordingProps) {
  const { t } = useTranslation()
  const [showAll, setShowAll] = useState(false)
  // One pass open at a time: opening another closes the first.
  const [openTrack, setOpenTrack] = useState<number | null>(null)
  if (people.length === 0) {
    return <p className="text-xs text-muted-foreground">{t(`${COPY}.none`)}</p>
  }
  const shown = showAll ? people : people.slice(0, FIRST)
  return (
    <div className="flex flex-col gap-2" data-testid="video-people">
      <ul className="flex flex-col gap-2">
        {shown.map(person => (
          <PersonRow
            key={`${person.track_id}:${person.face_id ?? ''}`}
            person={person}
            events={eventsOf(person, events)}
            selected={person.track_id === selectedTrackId}
            expanded={person.track_id === openTrack}
            onToggle={() =>
              setOpenTrack(current => (current === person.track_id ? null : person.track_id))
            }
            onPlayTrack={onPlayTrack}
            recordedAt={recordedAt}
          />
        ))}
      </ul>
      {people.length > shown.length ? (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="self-start"
          onClick={() => setShowAll(true)}
        >
          {t(`${COPY}.showAll`, { total: people.length })}
        </Button>
      ) : null}
    </div>
  )
}
